import { ChangeDetectorRef, Component, inject } from '@angular/core';
import type { NgxForm } from '../../shared/components/form/ngx-form.types';
import { RagDatasupplyFrontendService } from '@reflact/rag-datasupply';
import { User, Vkc } from '@reflact/reflactportal';
import { ToastrService } from 'ngx-toastr';
import { FaqService } from 'src/app/shared/services/faq.service';
import { UserService } from 'src/app/shared/services/user.service';
import { TileService } from 'src/app/shared/services/tile.service';
import { MandantService } from 'src/app/shared/services/mandant.service';
import { Tile } from 'src/app/shared/types/tile.types';

/** `User` (aus dem shared Package) kennt `tiles`/`tilePermissions` (individuelle Kachel-Grants +
 * kachel-eigene Zusatzrechte) noch nicht — die Felder leben rein im Portal (siehe
 * docs/mandanten-rollen-rechte.md, zweigleisiges Modell). */
type MandantUserWithTiles = User & { tiles: string[]; tilePermissions: Record<string, string[]> };

@Component({
  templateUrl: './user-administration.component.html',
  styleUrls: ['./user-administration.component.scss'],
  providers: [RagDatasupplyFrontendService],
  standalone: false
})
export class UserAdministrationComponent {
  public vkcs: Vkc[] = [];
  /** Voller Katalog — nur für die Anzeige bestehender Grants (`getTileById`), z.B. falls ein
   * Nutzer noch eine Kachel zugewiesen hat, die der Mandanten-Admin inzwischen deaktiviert hat. */
  public allTiles: Tile[] = [];
  /** Kacheln aus dem Mandanten-Pool, die nicht bereits für alle Nutzer freigegeben sind. */
  public assignableTiles: Tile[] = [];
  public permissionTiles: Tile[] = [];
  private sharedTiles: (Tile & { readonlyAssignment: boolean })[] = [];
  public readonly getSharedTilesForUser = (data: { permissions?: string[]; tiles?: string[] }): Tile[] =>
    this.sharedTiles.filter(tile => (tile.permissions ?? []).every(permission => data.permissions?.includes(permission)) || data.tiles?.includes(tile._id));
  public translatedLabels: NgxForm.TranslatedLabels = new Map<string, string>([
    ["globalUser.email", $localize`:@@globalEmail:Email`],
    ["globalUser.name", $localize`:@@globalName:Name`],
    ["permissions", $localize`:@@globalUserAdminPermissions:Berechtigungen`],
    ["tiles", $localize`:@@userAdminAvailableTools:Verfügbare Tools`],
    ["excludeCrmIds", $localize`:@@globalUserAdminExcludeCrmIds:Ausgeschaltete CRM-IDs`],
  ])

  private readonly cdr = inject(ChangeDetectorRef);
  public creating = false;

  constructor(public frontendService: RagDatasupplyFrontendService<User>, private userService: UserService, private tileService: TileService, private mandantService: MandantService, private toastrService: ToastrService, private faqService: FaqService) {
    this.userService.getMandantUsers().then(u => this.frontendService.fromArray(u));
    Promise.all([this.tileService.listTiles(), this.mandantService.getMyMandant()]).then(([allTiles, mandant]) => {
      this.allTiles = allTiles;
      // mandant.service.ts erwartet historisch bedingt ObjectId-Typen für `tiles`, tatsächlich
      // sind Kachel-Ids seit dem Logto-Umstieg einfache Strings (siehe mandant.types.ts).
      const poolTileIds = (mandant?.tiles ?? []) as unknown as string[];
      const activeTileIds = mandant?.activeTiles ?? [];
      this.assignableTiles = allTiles.filter(tile => poolTileIds.includes(tile._id) && !activeTileIds.includes(tile._id));
      this.sharedTiles = allTiles.filter(tile => poolTileIds.includes(tile._id) && activeTileIds.includes(tile._id)).map(tile => ({ ...tile, readonlyAssignment: true }));
      this.permissionTiles = allTiles.filter(tile => poolTileIds.includes(tile._id) && !!tile.customPermissions?.length);
    });
  }

  public getTileById(tileId: string): Tile | undefined {
    return this.allTiles.find(t => t._id === tileId);
  }

  /** Ob/wie ein zugewiesenes Kachel-Zusatzrecht (`tile.customPermissions`) aktuell für diesen
   * Nutzer gesetzt ist — Basis für die Checkboxen unter "Persönliche Kachelfreigaben". */
  public hasTilePermission(data: MandantUserWithTiles, tileId: string, key: string): boolean {
    return (data.tilePermissions?.[tileId] ?? []).includes(key);
  }

  public toggleTilePermission(data: MandantUserWithTiles, tileId: string, key: string): void {
    data.tilePermissions ??= {};
    const current = data.tilePermissions[tileId] ?? [];
    data.tilePermissions[tileId] = current.includes(key) ? current.filter(k => k !== key) : [...current, key];
  }


  /** Vereinfachtes Rollenmodell: nur noch "admin" (Mandanten-Admin) als zuweisbare Rolle - siehe
   * docs/mandanten-rollen-rechte.md. Andere, hier nicht verwaltete Rollen (z.B. "kitools-admin",
   * von anderen Apps über ihr eigenes Token gelesen) bleiben beim Toggeln unangetastet. */
  public isMandantAdmin(data: { permissions: string[] }): boolean {
    return data.permissions?.includes('admin') ?? false;
  }

  public toggleMandantAdmin(data: { permissions: string[] }, checked: boolean): void {
    const current = data.permissions ?? [];
    data.permissions = checked ? [...current.filter(p => p !== 'admin'), 'admin'] : current.filter(p => p !== 'admin');
  }

  /** Als Arrow-Function-Property (nicht Methode), damit `this` beim Aufruf über
   * `<app-user-management [saveUser]="performUpdateUser">` erhalten bleibt — der Kind-Komponente
   * gibt das zurückgegebene Bool Feedback direkt am Speichern-Button statt per Toastr. */
  public performUpdateUser = async (data: User): Promise<boolean> => {
    if (!data._id) {
      this.toastrService.error($localize`:@@globalSaveFailedNoId:Das Speichern ist fehlgeschlagen. Bitte lade die Seite neu und versuche es erneut.`);
      return false;
    }
    const tiles = (data as MandantUserWithTiles).tiles ?? [];
    const tilePermissions = (data as MandantUserWithTiles).tilePermissions ?? {};
    const [permissionsRes, tilesRes] = await Promise.all([
      this.userService.editMandantUser(data._id, data.permissions, data.excludeCrmIds),
      this.userService.editMandantUserTiles(data._id.toString(), tiles, tilePermissions),
    ]);
    this.userService.getMandantUsers().then(u => this.frontendService.fromArray(u))
    if (permissionsRes.status == "ok" && tilesRes.status == "ok") {
      return true;
    }
    this.toastrService.error($localize`:@@globalSaveFailedNoId:Das Speichern ist fehlgeschlagen. Bitte lade die Seite neu und versuche es erneut.`);
    return false;
  }

  public async createUser(data: any) {
    // Guards against a stray duplicate (createUserEvent) firing with empty data alongside the
    // real submit (see FormComponent#submitLock) — silently drops it instead of sending a
    // request that's guaranteed to fail validation on the backend.
    if (!data?.globalUser?.email || !data?.globalUser?.name) return;
    this.creating = true;
    this.cdr.detectChanges();
    let res;
    try {
      res = await this.userService.createUser(data.globalUser.email, data.globalUser.name, data.permissions, []);
    } finally {
      this.creating = false;
      this.cdr.detectChanges();
    }
    if (res.status == "ok") {
      this.toastrService.success($localize`:@@globalSaved:Gespeichert!`);
    }
    this.userService.getMandantUsers().then(u => this.frontendService.fromArray(u))
  }

  public async deleteUser(data: User) {
    if (!data._id) {
      this.toastrService.error($localize`:@@globalSaveFailedNoId:Das Speichern ist fehlgeschlagen. Bitte lade die Seite neu und versuche es erneut.`);
      return;
    }
    const res = await this.userService.deleteMandantUser(data._id);
    if (res.status == "ok") {
      this.toastrService.success($localize`:@@globalSaved:Gespeichert!`);
    }
    this.userService.getMandantUsers().then(u => this.frontendService.fromArray(u))
  }
}
